import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Loader2, Check, CalendarDays } from "lucide-react";
import { DELIVERY_OPTIONS } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];
const TIMES = ["10:00 AM", "12:00 PM", "2:00 PM", "4:00 PM", "6:00 PM", "8:00 PM"];

export default function BookingCalendar({ sessionId, tierName, initialName = "", initialEmail = "", mode = "book", onDone, fixedDelivery = null }) {
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [delivery, setDelivery] = useState(fixedDelivery || DELIVERY_OPTIONS[0]);
  const [phone, setPhone] = useState("");
  const [form, setForm] = useState({ name: initialName, email: initialEmail, note: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const confirm = async () => {
    if (!date || !time) return toast.error("Please pick a date and time.");
    if (!form.name.trim() || !form.email.trim() || !form.email.includes("@")) return toast.error("Please add your name and a valid email.");
    if (delivery === "Phone Call" && !phone.trim()) return toast.error("Please add a phone number for your call.");
    setLoading(true);
    const note = delivery === "Phone Call" && phone.trim()
      ? `Phone: ${phone.trim()}${form.note ? ` — ${form.note}` : ""}`
      : form.note;
    try {
      await axios.post(`${API}/appointments`, {
        session_id: sessionId,
        name: form.name,
        email: form.email,
        date: date.toDateString(),
        time,
        delivery_method: delivery,
        note,
        origin_url: window.location.origin,
      });
      setDone(true);
      if (onDone) onDone();
      toast.success(mode === "reschedule" ? "Your session has been moved ✦" : "Your session is booked ✦");
    } catch (e) {
      const msg = e.response?.status === 403 ? "Payment could not be verified for this booking." : "Could not confirm. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-sm bg-[#140B09] border border-[#C9A24B]/25 px-4 py-3 text-sm text-[#EFE3CC] placeholder:text-[#7a6a55] outline-none focus:border-[#C77E8A] focus:ring-1 focus:ring-[#C77E8A]/40 transition-all";

  if (done) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }} className="flex flex-col items-center text-center" data-testid="appointment-success">
        <div className="w-16 h-16 rounded-full border border-[#C9A24B]/40 flex items-center justify-center glow-gold mb-6">
          <Check className="w-7 h-7 text-[#C9A24B]" strokeWidth={1.5} />
        </div>
        <h2 className="font-display text-3xl text-[#EFE3CC]">{mode === "reschedule" ? "Your session has been moved" : "Your session is booked"}</h2>
        <p className="mt-3 text-[#B7A488] font-light max-w-sm">
          {tierName} · {date?.toDateString()} at {time} · via {delivery}. A confirmation is on its way — I'll meet you then ✦
        </p>
        {mode !== "reschedule" && (
          <a href={`/reschedule?session_id=${sessionId}`} data-testid="reschedule-link" className="mt-5 text-xs tracking-[0.15em] uppercase text-[#C9A24B] border-b border-transparent hover:border-[#C9A24B] transition-colors pb-1">
            Need a different time? Reschedule
          </a>
        )}
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }} className="w-full max-w-2xl" data-testid="booking-calendar">
      <div className="flex items-center justify-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#C9A24B] mb-2">
        <CalendarDays className="w-4 h-4" strokeWidth={1.5} /> Payment confirmed
      </div>
      <h2 className="text-center font-display text-3xl sm:text-4xl text-[#EFE3CC]">Schedule your <span className="italic text-gold-gradient">{tierName}</span></h2>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="flex flex-col items-center">
          <div className="gold-frame rounded-sm bg-[#1F130E]">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              data-testid="calendar"
            />
          </div>
        </div>

        <div>
          <label className="block text-[11px] tracking-[0.15em] uppercase text-[#B7A488] mb-2">Choose a time</label>
          <div className="grid grid-cols-3 gap-2 mb-5">
            {TIMES.map((t) => (
              <button
                key={t}
                onClick={() => setTime(t)}
                data-testid={`time-${t.replace(/[:\s]/g, "")}`}
                className={`rounded-full py-2.5 text-xs tracking-wide transition-all ${time === t ? "bg-gradient-to-r from-[#9C2A52] to-[#C77E8A] text-[#EFE3CC]" : "border border-[#C9A24B]/25 text-[#B7A488] hover:border-[#C9A24B]/60"}`}
              >
                {t}
              </button>
            ))}
          </div>

          <label className="block text-[11px] tracking-[0.15em] uppercase text-[#B7A488] mb-2">How would you like it delivered?</label>
          {fixedDelivery ? (
            <div className="mb-5 rounded-sm border border-[#C9A24B]/30 bg-[#140B09] px-4 py-3 text-sm text-[#E9D3A0]" data-testid="booking-delivery-fixed">
              Delivered via <strong>{fixedDelivery}</strong>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 mb-5" data-testid="booking-delivery-toggle">
              {DELIVERY_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  onClick={() => setDelivery(opt)}
                  data-testid={`booking-delivery-${opt.replace(/\s/g, "-").toLowerCase()}`}
                  className={`rounded-full py-2.5 px-2 text-[11px] tracking-wide transition-all ${delivery === opt ? "bg-gradient-to-r from-[#9C2A52] to-[#C77E8A] text-[#EFE3CC]" : "border border-[#C9A24B]/25 text-[#B7A488] hover:border-[#C9A24B]/60"}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          )}

          <div className="space-y-3">
            <input type="text" value={form.name} onChange={update("name")} data-testid="appt-name" placeholder="Your name" className={inputCls} />
            <input type="email" value={form.email} onChange={update("email")} data-testid="appt-email" placeholder="your@email.com" className={inputCls} />
            {delivery === "Phone Call" ? (
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="appt-phone" placeholder="Best phone number for your call" className={inputCls} />
            ) : null}
            <textarea value={form.note} onChange={update("note")} rows={3} data-testid="appt-note" placeholder="Anything you'd like me to know? (optional)" className={`${inputCls} resize-none`} />
          </div>

          <button
            onClick={confirm}
            disabled={loading}
            data-testid="appt-confirm"
            className="mt-5 w-full rounded-full bg-gradient-to-r from-[#9C2A52] to-[#C77E8A] py-3.5 text-xs tracking-[0.2em] uppercase text-[#EFE3CC] shadow-[0_0_30px_rgba(156,42,82,0.35)] transition-transform hover:scale-[1.02] disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Confirm My Session
          </button>
        </div>
      </div>
    </motion.div>
  );
}
